import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ScatterChart,
  Scatter
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

// Color palette for charts
const COLORS = {
  primary: '#3B82F6',
  secondary: '#10B981',
  accent: '#F59E0B',
  danger: '#EF4444',
  purple: '#8B5CF6',
  teal: '#14B8A6',
  orange: '#F97316',
  pink: '#EC4899',
};

const CHART_COLORS = [
  COLORS.primary,
  COLORS.secondary,
  COLORS.accent,
  COLORS.danger,
  COLORS.purple,
  COLORS.teal,
  COLORS.orange,
  COLORS.pink,
];

// Performance Metrics Chart
export const PerformanceChart = ({ metrics }) => {
  if (!metrics?.performance) return null;

  const data = [
    {
      name: 'Response Time',
      value: metrics.performance.response_time?.mean || 0,
      unit: 's',
      target: 2.0,
    },
    {
      name: 'Tokens/Sec',
      value: metrics.performance.tokens_per_second?.mean || 0,
      unit: 'tokens/s',
      target: 50,
    },
    {
      name: 'Success Rate',
      value: metrics.performance.throughput?.success_rate || 0,
      unit: '%',
      target: 95,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Performance Metrics</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip
              formatter={(value, name) => [
                `${value.toFixed(2)} ${data.find(d => d.name === name)?.unit || ''}`,
                name
              ]}
            />
            <Bar dataKey="value" fill={COLORS.primary} />
            <Bar dataKey="target" fill={COLORS.secondary} opacity={0.3} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

// Quality Distribution Pie Chart
export const QualityDistributionChart = ({ metrics }) => {
  if (!metrics?.quality?.quality_distribution) return null;

  const data = Object.entries(metrics.quality.quality_distribution).map(([key, value]) => ({
    name: key.charAt(0).toUpperCase() + key.slice(1),
    value,
    color: {
      excellent: COLORS.secondary,
      good: COLORS.primary,
      fair: COLORS.accent,
      poor: COLORS.danger,
    }[key] || COLORS.primary,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Quality Distribution</CardTitle>
      </CardHeader>
      <CardContent>
        {data.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-[300px] text-gray-500">
            No quality data available
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Cost Analysis Chart
export const CostAnalysisChart = ({ metrics }) => {
  if (!metrics?.cost) return null;

  const data = [
    {
      name: 'Total Cost',
      value: metrics.cost.total_cost || 0,
      type: 'total',
    },
    {
      name: 'Average Cost',
      value: metrics.cost.average_cost || 0,
      type: 'average',
    },
    {
      name: 'Cost per Token',
      value: (metrics.cost.cost_per_token?.mean || 0) * 1000, // Convert to per 1000 tokens
      type: 'per_token',
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cost Analysis</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip
              formatter={(value, name) => {
                const item = data.find(d => d.name === name);
                const unit = item?.type === 'per_token' ? '/1k tokens' : '$';
                return [`${unit}${value.toFixed(6)}`, name];
              }}
            />
            <Bar dataKey="value" fill={COLORS.accent} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

// Trend Analysis Chart
export const TrendAnalysisChart = ({ metrics }) => {
  if (!metrics?.trends || metrics.trends.insufficient_data) return null;

  const trends = [
    {
      name: 'Performance',
      trend: metrics.trends.performance_trend?.direction || 'stable',
      change: metrics.trends.performance_trend?.change_percent || 0,
    },
    {
      name: 'Quality',
      trend: metrics.trends.quality_trend?.direction || 'stable',
      change: metrics.trends.quality_trend?.change_percent || 0,
    },
    {
      name: 'Cost',
      trend: metrics.trends.cost_trend?.direction || 'stable',
      change: metrics.trends.cost_trend?.change_percent || 0,
    },
  ];

  const data = trends.map(trend => ({
    ...trend,
    value: Math.abs(trend.change),
    color: trend.change > 0 ?
      (trend.name === 'Cost' ? COLORS.danger : COLORS.secondary) :
      (trend.name === 'Cost' ? COLORS.secondary : COLORS.danger),
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Trend Analysis</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip
              formatter={(value, name) => {
                const trend = trends.find(t => t.name === name);
                const direction = trend?.change > 0 ? '↑' : trend?.change < 0 ? '↓' : '→';
                return [`${direction} ${value.toFixed(1)}%`, `${name} Trend`];
              }}
            />
            <Bar dataKey="value" fill={(entry) => entry.color} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

// Reliability Radar Chart
export const ReliabilityRadarChart = ({ metrics }) => {
  if (!metrics?.reliability) return null;

  const data = [
    {
      subject: 'Error Rate',
      A: 100 - (metrics.reliability.error_rate?.mean || 0) * 100,
      fullMark: 100,
    },
    {
      subject: 'Consistency',
      A: (metrics.reliability.consistency?.mean || 0) * 100,
      fullMark: 100,
    },
    {
      subject: 'Reliability Score',
      A: metrics.reliability.reliability_score || 0,
      fullMark: 100,
    },
    {
      subject: 'Success Rate',
      A: metrics.performance?.throughput?.success_rate || 0,
      fullMark: 100,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Reliability Assessment</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <RadarChart data={data}>
            <PolarGrid />
            <PolarAngleAxis dataKey="subject" />
            <PolarRadiusAxis angle={90} domain={[0, 100]} />
            <Radar
              name="Score"
              dataKey="A"
              stroke={COLORS.primary}
              fill={COLORS.primary}
              fillOpacity={0.3}
            />
            <Tooltip />
          </RadarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

// Distribution Analysis Chart
export const DistributionChart = ({ metrics }) => {
  if (!metrics?.distribution) return null;

  // Provider distribution
  const providerData = Object.entries(metrics.distribution.provider_distribution || {}).map(([key, value]) => ({
    name: key,
    value,
  }));

  // Model distribution
  const modelData = Object.entries(metrics.distribution.model_distribution || {}).map(([key, value]) => ({
    name: key,
    value,
  }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Provider Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          {providerData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={providerData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {providerData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[250px] text-gray-500">
              No provider data available
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Model Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          {modelData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={modelData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {modelData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[250px] text-gray-500">
              No model data available
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Comprehensive Metrics Overview
export const MetricsOverview = ({ metrics }) => {
  const overviewData = [
    {
      category: 'Performance',
      score: metrics?.performance?.response_time?.mean ?
        Math.max(0, 100 - (metrics.performance.response_time.mean * 20)) : 0,
      color: COLORS.primary,
    },
    {
      category: 'Quality',
      score: (metrics?.quality?.quality_score?.mean || 0) * 100,
      color: COLORS.secondary,
    },
    {
      category: 'Cost Efficiency',
      score: metrics?.cost?.cost_efficiency?.mean ?
        Math.min(100, (metrics.cost.cost_efficiency.mean * 20)) : 0,
      color: COLORS.accent,
    },
    {
      category: 'Reliability',
      score: metrics?.reliability?.reliability_score || 0,
      color: COLORS.purple,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Overall Metrics Overview</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={overviewData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="category" />
            <YAxis domain={[0, 100]} />
            <Tooltip formatter={(value) => [`${value.toFixed(1)}%`, 'Score']} />
            <Bar dataKey="score" fill={(entry) => entry.color} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

// Time Series Chart for historical data
export const TimeSeriesChart = ({ data, title = "Performance Over Time" }) => {
  if (!data || data.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {data.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="timestamp" />
              <YAxis />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="value"
                stroke={COLORS.primary}
                strokeWidth={2}
                dot={{ fill: COLORS.primary, strokeWidth: 2, r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-[300px] text-gray-500">
            No time series data available
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Scatter plot for correlation analysis
export const CorrelationChart = ({ data, xKey, yKey, title }) => {
  if (!data || data.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {data.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <ScatterChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={xKey} />
              <YAxis dataKey={yKey} />
              <Tooltip />
              <Scatter dataKey={yKey} fill={COLORS.primary} />
            </ScatterChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-[300px] text-gray-500">
            No correlation data available
          </div>
        )}
      </CardContent>
    </Card>
  );
};